// Manager Re-Approve popup body — same data resolution as ManagerApproveBody
// (resolveSubmissionFinance), plus the CFO's rejection reason: pulled from the
// submission's own activity log rather than a separate approval-request
// fetch, since workforceCFOApprovalController.go pushes a submissionActivity
// entry with activityType "CFO Rejected" and the real reason as
// activityComments every time a CFO rejects. The LAST such entry is "what the
// CFO said most recently" even across repeated reject/resubmit cycles.
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Spin, message } from 'antd';
import FinanceApprovalPanel from '../../../finance/FinanceApprovalPanel.jsx';
import { FINANCE_TYPE_SUBMISSION_MANAGER_REAPPROVE } from '../../../finance/financeApprovalConfig.js';
import { resolveSubmissionFinance, idOf } from '../../../finance/financeApprovalData.js';
import { approveSubmission, sendSubmissionForCfoApproval } from '../../../../services/popupApi';
import { getActivity } from '../../../../services/detailedViewApi';

// Same identity-building rule as ManagerApproveBody: resolved values (form
// groups + joins) win over the clicked row, which on the list/detail surface
// is flattened and often stale.
function buildIdentity(seed, display = {}) {
  return {
    client: display.clientName ?? seed.clientName,
    role: display.jobTitle ?? seed.jobTitle ?? seed.designation,
    reqId: display.reqId ?? seed.jobClientRefrenceId ?? seed.mspReqId ?? seed.submissionId,
    name: display.candidateName
      || [seed.firstName, seed.lastName].filter(Boolean).join(' ')
      || seed.candidateFullName,
    location: display.jobLocation ?? seed.city ?? seed.currentLocation ?? seed.jobLocation,
    vendor: display.employerName ?? seed.supplierName ?? seed.submitSource,
    submittedBy: display.submittedBy ?? seed.signatureEmail ?? seed.recruiterName,
    submittedAt: display.createdAt ?? seed.createdAt,
    email: display.email ?? seed.email,
    employerLocation: display.employerLocation,
    employerName: display.employerName,
  };
}

// lastCfoRejection — the newest submissionActivity entry the CFO reject
// endpoint stamped. Activity lists here come back OLDEST-first (see
// ActivityTab/timelineUtils elsewhere), so scan from the end; tolerate a
// stray case difference rather than require an exact literal match.
function lastCfoRejection(activity) {
  const rows = Array.isArray(activity) ? activity : [];
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const type = String(rows[i]?.activityType ?? '').trim().toLowerCase();
    if (type === 'cfo rejected') return rows[i];
  }
  return null;
}

// titleCase — capitalize each word, so a stored comment like "the rate is too
// high" reads as "The Rate Is Too High" in the rejection banner.
function titleCase(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export default function ManagerReapproveBody({ selectedIds, selectedRecords, onClose }) {
  const seed = selectedRecords?.[0] ?? {};
  const submissionId = selectedIds?.[0] ?? idOf(seed);
  const [resolved, setResolved] = useState(null);
  const [engagementId, setEngagementId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      resolveSubmissionFinance(submissionId, seed),
      getActivity('submission', submissionId).catch(() => []),
    ])
      .then(([data, activity]) => {
        if (cancelled) return;
        const rejection = lastCfoRejection(activity);
        // Linked onboarding engagement — the backend surfaces
        // onboarding.workforceEngagementId into recordMeta; fall back to the
        // clicked row in case it carried it. Needed to re-send to the CFO.
        setEngagementId(idOf(data?.meta?.workforceEngagementId
          ?? seed?.onboarding?.workforceEngagementId
          ?? seed?.workforceEngagementId));
        // Reason source, in order: the submission's own reject audit
        // (internalSubmissionStatus.rejectLog.rejectReasonComments, surfaced
        // by the backend into recordMeta = data.meta) → the last "CFO Rejected"
        // activity comment. Shown Title-Cased in the banner.
        const rawReason = data?.meta?.rejectReasonComments
          || rejection?.activityComments
          || '';
        setResolved({
          ...data,
          record: {
            ...data.record,
            submission: {
              ...data.record.submission,
              cfoRejectionReason: rawReason ? titleCase(rawReason) : '',
              cfoRejectedAt: data?.meta?.rejectedAt ?? rejection?.activityTime ?? null,
            },
          },
        });
      })
      .catch((error) => {
        if (cancelled) return;
        message.error(error?.message ?? 'Unable to load finance details');
        setResolved({ record: { submission: seed }, lookups: {}, display: {} });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const actions = useMemo(() => ({
    // "Send For Approval" — the re-approval's single forward action. It calls
    // BOTH APIs: re-send the engagement to the CFO first (the critical step —
    // if it fails the submission stays "CFO Rejected" so the manager can
    // retry), then re-approve the submission to record the fixed proposed rate.
    submissionReapproveSend: async ({ computed }) => {
      await sendSubmissionForCfoApproval(engagementId, {
        submissionId,
        billRate: computed.proposedRate,
        payRate: computed.employerRate,
        // Currency as STORED (computed.currencyRaw), not the symbol the panel
        // renders — the CFO request must carry the tenant's own code/symbol
        // rather than a value normalised for display.
        currency: computed.currencyRaw ?? computed.currency,
        // The unit as RESOLVED, for the same reason as the currency above: the
        // service used to hardcode 'hr' in this request body, so a monthly or
        // yearly engagement was described to the CFO as hourly — a right number
        // against a wrong unit, which reads as a completely different amount.
        rateUnit: computed.rateUnit,
        // Do not send referral data from the manager re-approval popup.
        // The onboarding referral already stored on workforceEngagements must
        // remain unchanged and will be copied into the new CFO request by the backend.
        vmsPercent: computed.clientVmsPercent,
      });
      // context:'resend' tells the shared /submissions/approve handler this is
      // a rate-correction RESEND (after a CFO rejection), not a fresh approval —
      // it logs a distinct "resent_for_approval" activity entry instead of
      // "approved", so the timeline doesn't show two approvals for one request.
      // Unit + currency written back AS RESOLVED — a literal 'hr' here would
      // overwrite a daily/monthly rate's unit every time it was re-approved.
      await approveSubmission(submissionId, {
        candidateProposedRate: computed.proposedRate,
        candidateProposedRateCurrency: computed.currencyRaw ?? computed.currency,
        candidateProposedRateUnit: computed.rateUnit,
        submitVia: 'Mail',
        context: 'resend',
      });
      message.success('Re-approved and sent for CFO approval');
      onClose?.({ success: true });
    },
  }), [submissionId, engagementId, onClose]);

  if (loading || !resolved) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin size="large" /></div>;
  }

  return (
    <FinanceApprovalPanel
      type={FINANCE_TYPE_SUBMISSION_MANAGER_REAPPROVE}
      record={resolved.record}
      lookups={resolved.lookups}
      identity={buildIdentity(seed, resolved.display)}
      currentUserId={typeof localStorage !== 'undefined' ? localStorage.getItem('userId') ?? undefined : undefined}
      actions={actions}
      onClose={() => onClose?.()}
      variant="popup"
    />
  );
}

ManagerReapproveBody.propTypes = {
  selectedIds: PropTypes.array,
  selectedRecords: PropTypes.array,
  onClose: PropTypes.func,
};
